import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import {
  init,
  captureException,
  Sentry,
} from '@douro-make/observability/react-spa';

// Boot the shared SDK at module-evaluation time.
//
// Read `import.meta.env.VITE_*` STATICALLY here on the consumer side so
// Vite's define transform inlines the values into the browser bundle at
// build time. The shared package can't do this internally — dynamic
// `import.meta.env[key]` lookups don't survive Vite's bundling and the
// browser saw `import.meta.env` as effectively empty, producing
// "VITE_SENTRY_DSN is not set" in the console. Per @douro-make/
// observability ≥ 0.0.6, pass the values as explicit init params.
//
// The `reactRouterV7BrowserTracingIntegration` is SPA-specific and lives
// outside the package's defaults; passing it through `integrations` is an
// explicit opt-in by the consumer (per the package's react-spa contract).
// Tunnel envelopes through a same-origin path so ad-blockers / privacy
// extensions (uBlock Origin, Brave Shields, EasyPrivacy lists) can't
// pattern-match the `/api/<id>/envelope/` path on `app.glitchtip.com`
// and return `ERR_BLOCKED_BY_CLIENT` at the browser layer. Paired with
// the `/monitoring/:path*` → `https://app.glitchtip.com/:path*` Vercel
// rewrite in `vercel.json`. DSN parses to `https://<key>@host/<projectId>`,
// so derive the project ID and build the canonical tunnel path.
const DSN = import.meta.env.VITE_SENTRY_DSN;
let tunnel;
try {
  if (DSN) {
    const projectId = new URL(DSN).pathname.replace(/^\//, '');
    if (projectId) tunnel = `/monitoring/api/${projectId}/envelope/`;
  }
} catch {
  // Malformed DSN — fall back to direct upstream POST (Sentry SDK
  // default). Ad-blocker breakage is preferable to a hard crash here.
}

init({
  service: 'cms',
  dsn: DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
  tracesSampleRate: parseFloat(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0',
  ),
  tunnel,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
});

// One-shot scope tag at boot: seed the correlation_id tag from sessionStorage
// so events fired before the first Axios round-trip still carry the ID.
// The Axios response interceptor re-tags after the first backend round-trip
// if the backend echoed a different (canonical) ID.
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
if (typeof window !== 'undefined') {
  let id = window.sessionStorage.getItem('dm_correlation');
  if (!id || !UUID_V4.test(id)) {
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : null;
    if (id) window.sessionStorage.setItem('dm_correlation', id);
  }
  if (id && UUID_V4.test(id)) {
    Sentry.getCurrentScope().setTag('correlation_id', id);
  }
}

/**
 * Backwards-compatible gate flag for AuthContext.
 *
 * The shared package's `init` already centralises the no-op gate; this
 * export exists so existing call sites (AuthContext.jsx) don't need to be
 * touched to import `isInitialized()` from the package.
 */
export const isSentryEnabled = Boolean(
  import.meta.env.VITE_SENTRY_DSN &&
    import.meta.env.VITE_SENTRY_ENVIRONMENT &&
    import.meta.env.VITE_SENTRY_ENVIRONMENT !== 'local',
);

/**
 * Legacy export — kept so `main.jsx` doesn't need a touch. Now a no-op
 * because the shared `init()` above runs at module-evaluation time.
 */
export function initSentry() {
  /* no-op — initialization happens at import time via init() above */
}

/**
 * Backwards-compatible wrapper around the shared `captureException` helper.
 *
 * Existing call sites (services/api.js, contexts/AuthContext.jsx) pass
 * `{ tags, context }`; new fields `area`, `flow`, `level` map directly to
 * the shared package's contract. When the package skipped init (no DSN or
 * VITE_SENTRY_ENVIRONMENT === 'local'), captureException falls back to
 * `console.error` with the full structured payload — not a silent no-op.
 */
export function captureError(error, options = {}) {
  const { tags, context, area, flow, level } = options;

  // Preserve the prior "keyed context" UX (Sentry's setContext panel) by
  // attaching context entries to the current scope before delegating. Safe
  // to call even when Sentry.init was skipped; the SDK no-ops.
  if (context) {
    Object.entries(context).forEach(([key, value]) =>
      Sentry.getCurrentScope().setContext(key, value),
    );
  }

  captureException(error, {
    area: area ?? 'api_client',
    flow: flow ?? 'unknown',
    level: level ?? 'error',
    tags: tags ?? {},
  });
}
